import { useState, useEffect, useRef, useCallback } from "react"
import { useUser } from "../contexts/UserContext"
import { useAuth } from "../contexts/AuthProvider"
import { useNavigate, useParams } from "react-router-dom"
import CandidateProfileSidebar from "../components/CandidateProfileSidebar"
import ProfileSidebar from "../components/ProfileSideBar"
import SidebarLoading from "../components/SidebarLoading"
import NoPermissionOverlay from "../components/NoPermissionOverlay"
import {
  sendMessage,
  markMessagesAsRead,
  getOrganizationConversations,
  getApplicantConversations,
  subscribeToConversationIds,
  subscribeToConversation,
  subscribeToMessages,
} from "../services/messageService"
import { getDoc, doc } from "firebase/firestore"
import { db } from "../services/firebase"
import EmailThreads from "./EmailThreads"
import EmailThreadView from "./EmailThreadView"

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

const InboxPage = () => {
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [metadata, setMetadata] = useState({})
  const [loadingConversations, setLoadingConversations] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showReply, setShowReply] = useState(false)

  const { isInOrganization, organizationId, userData, isInterviewer } = useUser()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { conversationId } = useParams()

  const messagesEndRef = useRef(null)
  const convListenersRef = useRef(new Map())
  const idsListenerRef = useRef(null)
  const msgsListenerRef = useRef(null)

  useEffect(() => {
    if (!loadingMessages) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, loadingMessages])

  const attachConversationListener = useCallback((id) => {
    if (convListenersRef.current.has(id)) return
    const unsub = subscribeToConversation(id, (updated) => {
      setConversations((prev) => {
        const exists = prev.some((c) => c.id === updated.id)
        const next = exists ? prev.map((c) => (c.id === updated.id ? updated : c)) : [updated, ...prev]
        return [...next].sort((a, b) => (b.lastMessageAt?.toMillis() || 0) - (a.lastMessageAt?.toMillis() || 0))
      })
    })
    convListenersRef.current.set(id, unsub)
  }, [])

  const detachAllConversationListeners = useCallback(() => {
    convListenersRef.current.forEach((unsub) => unsub())
    convListenersRef.current.clear()
  }, [])

  useEffect(() => {
    if (!user?.uid) return
    const participantId = isInOrganization ? organizationId : user.uid
    if (!participantId) return
    let cancelled = false

    const setup = async () => {
      setLoadingConversations(true)
      detachAllConversationListeners()
      if (idsListenerRef.current) { idsListenerRef.current(); idsListenerRef.current = null }
      try {
        const fetched = isInOrganization
          ? await getOrganizationConversations(organizationId)
          : await getApplicantConversations(user.uid)
        if (cancelled) return
        setConversations(fetched)
        setLoadingConversations(false)
        fetched.forEach((c) => attachConversationListener(c.id))
        idsListenerRef.current = subscribeToConversationIds(participantId, isInOrganization, async (newIds) => {
          const newDocs = await Promise.all(
            newIds.map((id) => getDoc(doc(db, "conversations", id)).then((s) => s.exists() ? { id: s.id, ...s.data() } : null))
          )
          const valid = newDocs.filter(Boolean)
          if (!valid.length) return
          setConversations((prev) =>
            [...valid, ...prev].sort((a, b) => (b.lastMessageAt?.toMillis() || 0) - (a.lastMessageAt?.toMillis() || 0))
          )
          valid.forEach((c) => attachConversationListener(c.id))
        })
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load conversations:", err)
        setConversations([])
        setLoadingConversations(false)
      }
    }

    setup()
    return () => {
      cancelled = true
      detachAllConversationListeners()
      if (idsListenerRef.current) { idsListenerRef.current(); idsListenerRef.current = null }
    }
  }, [user?.uid, isInOrganization, organizationId])

  // Sync selectedConversation from URL param
  useEffect(() => {
    if (!conversationId) { setSelectedConversation(null); return }
    const found = conversations.find((c) => c.id === conversationId)
    if (found) setSelectedConversation(found)
  }, [conversationId, conversations])

  useEffect(() => {
    if (msgsListenerRef.current) { msgsListenerRef.current(); msgsListenerRef.current = null }
    if (!selectedConversation) { setMessages([]); setMetadata({}); return }
    setLoadingMessages(true)
    msgsListenerRef.current = subscribeToMessages(
      selectedConversation.id,
      (liveMessages, liveMetadata) => {
        setMetadata(liveMetadata)
        setMessages((prev) => {
          const confirmedIds = new Set(liveMessages.map((m) => m.id))
          const pendingTemps = prev.filter((m) => m.isTemp && !confirmedIds.has(m.id))
          return [...liveMessages, ...pendingTemps]
        })
        setLoadingMessages(false)
      },
      (err) => {
        console.error("Messages listener error:", err)
        setMessages([]); setMetadata({}); setLoadingMessages(false)
      }
    )
    if (user?.uid) {
      const readerId = isInOrganization ? organizationId : user.uid
      markMessagesAsRead(selectedConversation.id, readerId).catch(console.error)
    }
    return () => { if (msgsListenerRef.current) { msgsListenerRef.current(); msgsListenerRef.current = null } }
  }, [selectedConversation?.id])

  const handleSendMessage = useCallback(async (messageHtml) => {
    if (!messageHtml?.trim() || sendingMessage || !selectedConversation) return
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = messageHtml
    const messageText = tempDiv.textContent || tempDiv.innerText || ""
    if (!messageText.trim()) return

    setShowReply(false)
    setSendingMessage(true)

    const tempId = `temp_${Date.now()}`
    setMessages((prev) => [
      ...prev,
      {
        id: tempId, text: messageText,
        senderId: isInOrganization ? organizationId : user.uid,
        senderType: isInOrganization ? "organization" : "applicant",
        timestamp: new Date(), isTemp: true,
        senderName: isInOrganization ? metadata.organizationName : metadata.candidateName,
        senderAvatar: isInOrganization ? metadata.logoUrl : metadata.candidateAvatar,
      },
    ])

    try {
      await sendMessage(
        selectedConversation.organizationId,
        selectedConversation.applicantId,
        messageText,
        isInOrganization ? selectedConversation.organizationId : user.uid,
      )
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
    } catch (err) {
      console.error("Failed to send message:", err)
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
    } finally {
      setSendingMessage(false)
    }
  }, [sendingMessage, selectedConversation, isInOrganization, organizationId, user, metadata])

  const handleSelectConversation = useCallback((conversation) => {
    setShowReply(false)
    setSelectedConversation(conversation)
    navigate(`/inbox/${conversation.id}`)
    const myId = isInOrganization ? organizationId : user?.uid
    if (conversation.lastMessage?.senderId !== myId) {
      setConversations((prev) =>
        prev.map((c) => c.id === conversation.id ? { ...c, lastMessage: { ...c.lastMessage, read: true } } : c)
      )
    }
  }, [isInOrganization, organizationId, user, navigate])

  const handleBack = () => {
    setSelectedConversation(null)
    setShowReply(false)
    navigate("/inbox")
  }

  const filteredConversations = conversations.filter((conv) => {
    const q = searchQuery.toLowerCase()
    return (
      (conv.metadata?.organizationName || "").toLowerCase().includes(q) ||
      (conv.metadata?.candidateName || "").toLowerCase().includes(q)
    )
  })

  if (!userData) {
    return (
      <div style={{ display: "flex", fontFamily: FONT }}>
        <SidebarLoading />
        <div style={{ flex: 1, marginLeft: 256, paddingTop: 64, display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
          <div style={{ width: 20, height: 20, border: "2px solid #e8eaed", borderTop: "2px solid #1a73e8", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", fontFamily: FONT, background: "#f6f8fc" }}>
      {/* Sidebar */}
      <div style={{ flexShrink: 0, position: "fixed", inset: "0 auto 0 0", zIndex: 50 }}>
        {isInOrganization
          ? <ProfileSidebar activeTab="inbox" setActiveTab={() => {}} />
          : <CandidateProfileSidebar activeTab="inbox" setActiveTab={() => {}} />
        }
      </div>

      {/* Main area */}
      <div style={{ flex: 1, marginLeft: 240, display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {!selectedConversation ? (
            <EmailThreads
              conversations={conversations}
              filteredConversations={filteredConversations}
              loadingConversations={loadingConversations}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isInOrganization={isInOrganization}
              organizationId={organizationId}
              userId={user?.uid}
              onSelectConversation={handleSelectConversation}
            />
          ) : (
            <EmailThreadView
              messages={messages}
              metadata={metadata}
              loadingMessages={loadingMessages}
              sendingMessage={sendingMessage}
              showReply={showReply}
              setShowReply={setShowReply}
              isInOrganization={isInOrganization}
              filteredConversations={filteredConversations}
              selectedConversation={selectedConversation}
              onBack={handleBack}
              onSendMessage={handleSendMessage}
              onSelectConversation={handleSelectConversation}
              messagesEndRef={messagesEndRef}
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #dadce0; border-radius: 3px; }
      `}</style>


      <NoPermissionOverlay isOpen={isInterviewer} onClose={() => navigate("/interviews")} />
    </div>
  )
}

export default InboxPage